import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Pencil, Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/api";

const STATUS_COLORS = { 未確認: "text-amber-400", 承認: "text-green-400", 差戻し: "text-red-400" };
const INS_COLORS = { 未着手: "text-gray-400", 作業中: "text-accent", 完了: "text-green-400", 保留: "text-amber-400" };
const TYPE_LABELS = { work: "作業", off: "休日", leave: "休暇" };
const PHASE_COLORS = { 未着手: "text-gray-400", 進行中: "text-accent", 完了: "text-green-400" };

const inputCls = "w-full border border-line bg-ink px-3 py-2.5 text-sm text-white outline-none transition-colors focus:border-accent";
const EMPTY_PHASE = { name: "", start: "", end: "", status: "未着手", order: 0, notes: "" };
const EMPTY_COST = { date: "", category: "労務費", description: "", amount: "", note: "" };
const COST_CATEGORIES = ["労務費", "材料費", "外注費", "機械費", "その他"];

function Section({ title, testid, count, children }) {
  return (
    <section data-testid={testid} className="border border-line">
      <h2 className="flex items-center justify-between border-b border-line bg-coal px-4 py-3 font-mincho text-base font-bold text-white">
        {title}
        <span className="font-mono text-[10px] tracking-[0.2em] text-gray-500">{count}件</span>
      </h2>
      <div className="p-4">{children}</div>
    </section>
  );
}

export default function AdminProjectDetail() {
  const { projectId } = useParams();
  const [data, setData] = useState(null);
  const [phaseEditing, setPhaseEditing] = useState(null);
  const [phaseForm, setPhaseForm] = useState(EMPTY_PHASE);
  const [progForm, setProgForm] = useState({ date: "", percent: "", note: "" });
  const [costEditing, setCostEditing] = useState(null);
  const [costForm, setCostForm] = useState(EMPTY_COST);
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get(`/admin/projects/${projectId}/detail`).then((r) => setData(r.data)).catch(() => {});
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const savePhase = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...phaseForm, order: Number(phaseForm.order) || 0 };
    try {
      if (phaseEditing.id) await api.put(`/admin/projects/${projectId}/phases/${phaseEditing.id}`, payload);
      else await api.post(`/admin/projects/${projectId}/phases`, payload);
      toast.success("保存しました");
      setPhaseEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const removePhase = async (ph) => {
    if (!window.confirm(`工程「${ph.name}」を削除しますか？`)) return;
    try {
      await api.delete(`/admin/projects/${projectId}/phases/${ph.id}`);
      toast.success("削除しました");
      load();
    } catch {
      toast.error("削除に失敗しました");
    }
  };

  const addProgress = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/admin/projects/${projectId}/progress`, {
        date: progForm.date,
        percent: Number(progForm.percent) || 0,
        note: progForm.note,
      });
      toast.success("進捗を記録しました");
      setProgForm({ date: "", percent: "", note: "" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const removeProgress = async (entry) => {
    if (!window.confirm(`${entry.date}の進捗（${entry.percent}%）を削除しますか？`)) return;
    try {
      await api.delete(`/admin/projects/${projectId}/progress/${entry.id}`);
      toast.success("削除しました");
      load();
    } catch {
      toast.error("削除に失敗しました");
    }
  };

  const saveCost = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...costForm, amount: Number(costForm.amount) || 0 };
    try {
      if (costEditing.id) await api.put(`/admin/projects/${projectId}/costs/${costEditing.id}`, payload);
      else await api.post(`/admin/projects/${projectId}/costs`, payload);
      toast.success("保存しました");
      setCostEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const removeCost = async (c) => {
    if (!window.confirm(`原価「${c.description}」（${yen(c.amount)}）を削除しますか？`)) return;
    try {
      await api.delete(`/admin/projects/${projectId}/costs/${c.id}`);
      toast.success("削除しました");
      load();
    } catch {
      toast.error("削除に失敗しました");
    }
  };

  if (!data) {
    return <p className="font-mono text-sm tracking-[0.3em] text-gray-500">LOADING...</p>;
  }

  const { project, customer_name, staff, sites, schedules, instructions, photos, phases = [], progress = [], costs = [], daily_reports = [] } = data;
  const upcoming = schedules.filter((s) => s.date >= new Date().toISOString().slice(0, 10) && s.status !== "中止");
  const past = schedules.filter((s) => !upcoming.includes(s));
  const today = new Date().toISOString().slice(0, 10);
  const totalCost = costs.reduce((sum, c) => sum + (c.amount || 0), 0);
  const profit = (project.contract_amount || 0) - totalCost;
  const profitRate = project.contract_amount ? Math.round((profit / project.contract_amount) * 100) : null;
  const yen = (v) => `¥${Number(v || 0).toLocaleString()}`;

  return (
    <div data-testid="admin-project-detail">
      <Link to="/admin/projects" data-testid="project-detail-back" className="flex items-center gap-2 text-xs text-gray-500 transition-colors hover:text-accent">
        <ArrowLeft className="h-4 w-4" />
        案件一覧に戻る
      </Link>

      <Link to={`/admin/workflow/${projectId}`} data-testid="project-open-workflow" className="mt-4 inline-flex border border-accent/50 px-4 py-3 text-sm text-accent transition-colors hover:bg-accent/10">受付・見積・納品・請求をまとめて開く →</Link>

      <div className="mt-4 border border-line bg-panel p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="font-mono text-[10px] tracking-[0.3em] text-gray-500">{project.code || "—"}</p>
            <h1 data-testid="project-detail-name" className="mt-1 font-mincho text-2xl font-bold text-white">{project.name}</h1>
            <p className="mt-2 text-xs text-gray-400">
              {customer_name && <>発注者: {customer_name} ・ </>}
              期間: {project.start_planned || "?"} 〜 {project.end_planned || "?"}
              {project.address && <> ・ {project.address}</>}
            </p>
          </div>
          <span className={`border px-3 py-1.5 text-xs font-bold ${project.status === "施工中" ? "border-accent/50 text-accent" : project.status === "完了" ? "border-green-500/50 text-green-400" : "border-line text-gray-400"}`}>
            {project.status}
          </span>
        </div>
        <div className="mt-4 flex flex-wrap gap-1.5">
          {staff.map((s) => (
            <span key={s.id} data-testid={`project-detail-staff-${s.id}`} className="border border-line bg-ink px-2.5 py-1 text-xs text-gray-300">{s.display_name || s.name}</span>
          ))}
          {staff.length === 0 && <span className="text-xs text-gray-600">担当スタッフ未設定</span>}
        </div>

        <div data-testid="project-detail-contract" className="mt-5 border-t border-line pt-4">
          <div className="grid gap-x-8 gap-y-2 text-xs sm:grid-cols-2 lg:grid-cols-4">
            <p><span className="text-gray-500">契約金額: </span><span className="font-bold text-white">{project.contract_amount ? `${Number(project.contract_amount).toLocaleString()}円` : "—"}</span></p>
            <p><span className="text-gray-500">契約日: </span><span className="text-gray-300">{project.contract_date || "—"}</span></p>
            <p><span className="text-gray-500">契約方式: </span><span className="text-gray-300">{project.contract_type || "—"}</span></p>
            <p><span className="text-gray-500">注文番号: </span><span className="text-gray-300">{project.order_number || "—"}</span></p>
            <p><span className="text-gray-500">支払条件: </span><span className="text-gray-300">{project.payment_terms || "—"}</span></p>
            <p><span className="text-gray-500">完成検査日: </span><span className="text-gray-300">{project.inspection_date || "—"}</span></p>
            <p><span className="text-gray-500">実行予算: </span><span className="text-gray-300">{project.budget_amount ? `${Number(project.budget_amount).toLocaleString()}円` : "—"}</span></p>
          </div>
          <div className="mt-3 flex items-center gap-3">
            <div className="h-2 w-full max-w-xs overflow-hidden rounded-full bg-line">
              <div data-testid="project-detail-progress-bar" className="h-full bg-accent transition-all" style={{ width: `${Math.min(100, Math.max(0, project.progress_percent || 0))}%` }} />
            </div>
            <span data-testid="project-detail-progress" className="font-mono text-sm font-bold text-accent">{project.progress_percent || 0}%</span>
          </div>
        </div>
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <Section title="工程表" testid="project-detail-phases" count={phases.length}>
          <div className="mb-3 flex justify-end">
            <button data-testid="phase-add-button" onClick={() => { setPhaseForm({ ...EMPTY_PHASE, order: phases.length }); setPhaseEditing({}); }} className="flex items-center gap-1.5 border border-accent/50 px-3 py-1.5 text-[11px] font-bold text-accent transition-colors hover:bg-accent/10">
              <Plus className="h-3.5 w-3.5" />
              工程を追加
            </button>
          </div>
          <div className="space-y-1.5">
            {phases.map((ph) => (
              <div key={ph.id} data-testid={`phase-row-${ph.id}`} className="flex items-center gap-3 border border-line bg-ink px-3 py-2">
                <span className="font-mono text-[11px] text-gray-500">{ph.start || "?"} 〜 {ph.end || "?"}</span>
                <span className="text-xs font-bold text-white">{ph.name}</span>
                {ph.start && ph.end && today >= ph.start && today <= ph.end && ph.status !== "完了" && (
                  <span className="border border-accent/40 px-1.5 py-0.5 text-[9px] font-bold text-accent">本日期間</span>
                )}
                <span className={`ml-auto text-[10px] font-bold ${PHASE_COLORS[ph.status]}`}>{ph.status}</span>
                <button data-testid={`phase-edit-${ph.id}`} onClick={() => { setPhaseForm({ ...EMPTY_PHASE, ...ph }); setPhaseEditing(ph); }} className="p-1 text-gray-500 transition-colors hover:text-accent"><Pencil className="h-3.5 w-3.5" /></button>
                <button data-testid={`phase-delete-${ph.id}`} onClick={() => removePhase(ph)} className="p-1 text-gray-500 transition-colors hover:text-red-400"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            ))}
            {phases.length === 0 && <p className="text-xs text-gray-600">工程が登録されていません。「工程を追加」から作ってください。</p>}
          </div>
        </Section>

        <Section title="進捗管理" testid="project-detail-progress-section" count={progress.length}>
          <form data-testid="progress-form" onSubmit={addProgress} className="mb-4 flex flex-wrap items-end gap-2 border border-line bg-ink p-3">
            <label className="block">
              <span className="font-mono text-[9px] tracking-[0.2em] text-gray-500">日付</span>
              <input data-testid="progress-form-date" type="date" value={progForm.date} onChange={(e) => setProgForm((f) => ({ ...f, date: e.target.value }))} className={`mt-1 ${inputCls}`} />
            </label>
            <label className="block w-24">
              <span className="font-mono text-[9px] tracking-[0.2em] text-gray-500">進捗率%</span>
              <input data-testid="progress-form-percent" type="number" min="0" max="100" required value={progForm.percent} onChange={(e) => setProgForm((f) => ({ ...f, percent: e.target.value }))} className={`mt-1 ${inputCls}`} />
            </label>
            <label className="block min-w-[160px] flex-1">
              <span className="font-mono text-[9px] tracking-[0.2em] text-gray-500">メモ</span>
              <input data-testid="progress-form-note" value={progForm.note} onChange={(e) => setProgForm((f) => ({ ...f, note: e.target.value }))} className={`mt-1 ${inputCls}`} placeholder="例：基礎工事が完了" />
            </label>
            <button data-testid="progress-form-submit" type="submit" disabled={saving || progForm.percent === ""} className="bg-accent px-4 py-2.5 font-display text-[11px] font-bold tracking-[0.15em] text-ink transition-colors hover:bg-accent-hover disabled:opacity-40">
              記録
            </button>
          </form>
          <div className="space-y-1.5">
            {progress.map((entry) => (
              <div key={entry.id} data-testid={`progress-row-${entry.id}`} className="flex items-center gap-3 border border-line bg-ink px-3 py-2">
                <span className="font-mono text-[11px] text-gray-500">{entry.date}</span>
                <span className="font-mono text-xs font-bold text-accent">{entry.percent}%</span>
                <span className="flex-1 truncate text-xs text-gray-300">{entry.note}</span>
                <button data-testid={`progress-delete-${entry.id}`} onClick={() => removeProgress(entry)} className="p-1 text-gray-500 transition-colors hover:text-red-400"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            ))}
            {progress.length === 0 && <p className="text-xs text-gray-600">進捗の記録はまだありません。</p>}
          </div>
        </Section>
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <Section title="現場" testid="project-detail-sites" count={sites.length}>
          <div className="space-y-2">
            {sites.map((s) => (
              <div key={s.id} data-testid={`project-site-${s.id}`} className="flex items-center justify-between border border-line bg-ink px-3 py-2.5">
                <span className="text-xs font-bold text-white">{s.name}</span>
                <span className="text-[10px] text-gray-500">{s.address}</span>
              </div>
            ))}
            {sites.length === 0 && <p className="text-xs text-gray-600">現場が登録されていません。</p>}
          </div>
        </Section>

        <Section title="作業指示" testid="project-detail-instructions" count={instructions.length}>
          <div className="space-y-2">
            {instructions.map((i) => (
              <div key={i.id} data-testid={`project-instruction-${i.id}`} className="flex items-center justify-between border border-line bg-ink px-3 py-2.5">
                <div>
                  <span className="text-xs font-bold text-white">{i.title}</span>
                  <span className="ml-2 text-[10px] text-gray-500">{i.work_type}</span>
                </div>
                <span className={`text-[10px] font-bold ${INS_COLORS[i.status]}`}>{i.status}</span>
              </div>
            ))}
            {instructions.length === 0 && <p className="text-xs text-gray-600">作業指示が登録されていません。</p>}
          </div>
        </Section>
      </div>

      <div className="mt-6">
        <Section title="作業予定" testid="project-detail-schedules" count={schedules.length}>
          {upcoming.length > 0 && (
            <>
              <p className="mb-2 font-mono text-[10px] tracking-[0.2em] text-accent">今後の予定</p>
              <div className="mb-4 space-y-1.5">
                {upcoming.map((s) => (
                  <div key={s.id} data-testid={`project-schedule-${s.id}`} className="flex items-center gap-3 border border-line bg-ink px-3 py-2">
                    <span className="font-mono text-[11px] text-accent">{s.date}</span>
                    <span className="font-mono text-[11px] text-gray-500">{s.all_day ? "終日" : `${s.start_time}-${s.end_time}`}</span>
                    <span className="text-xs font-bold text-white">{s.title}</span>
                    <span className="text-[10px] text-gray-500">{TYPE_LABELS[s.type]}</span>
                    <span className={`ml-auto text-[10px] ${s.status === "中止" ? "text-red-400 line-through" : "text-gray-500"}`}>{s.status}</span>
                  </div>
                ))}
              </div>
            </>
          )}
          {past.length > 0 && (
            <>
              <p className="mb-2 font-mono text-[10px] tracking-[0.2em] text-gray-500">過去の予定</p>
              <div className="space-y-1.5 opacity-60">
                {past.slice(0, 10).map((s) => (
                  <div key={s.id} className="flex items-center gap-3 border border-line bg-ink px-3 py-2">
                    <span className="font-mono text-[11px] text-gray-500">{s.date}</span>
                    <span className="text-xs text-gray-400">{s.title}</span>
                    {s.done && <span className="ml-auto text-[10px] font-bold text-green-400">完了報告あり</span>}
                  </div>
                ))}
              </div>
            </>
          )}
          {schedules.length === 0 && <p className="text-xs text-gray-600">予定が登録されていません。</p>}
        </Section>
      </div>

      <div className="mt-6">
        <Section title="原価・利益" testid="project-detail-costs" count={costs.length}>
          <div className="mb-4 grid grid-cols-2 gap-2 text-center md:grid-cols-4">
            <div className="border border-line bg-ink p-3"><p className="font-mono text-[9px] text-gray-500">契約金額</p><p className="font-mono text-sm font-bold text-white">{project.contract_amount ? yen(project.contract_amount) : "—"}</p></div>
            <div className="border border-line bg-ink p-3"><p className="font-mono text-[9px] text-gray-500">原価合計</p><p data-testid="cost-total" className="font-mono text-sm font-bold text-amber-400">{yen(totalCost)}</p></div>
            <div className="border border-line bg-ink p-3"><p className="font-mono text-[9px] text-gray-500">利益</p><p data-testid="cost-profit" className={`font-mono text-sm font-bold ${profit >= 0 ? "text-green-400" : "text-red-400"}`}>{project.contract_amount ? yen(profit) : "—"}</p></div>
            <div className="border border-line bg-ink p-3"><p className="font-mono text-[9px] text-gray-500">利益率</p><p data-testid="cost-profit-rate" className={`font-mono text-sm font-bold ${(profitRate ?? 0) >= 0 ? "text-green-400" : "text-red-400"}`}>{profitRate !== null ? `${profitRate}%` : "—"}</p></div>
          </div>
          <div className="mb-3 flex justify-end">
            <button data-testid="cost-add-button" onClick={() => { setCostForm(EMPTY_COST); setCostEditing({}); }} className="flex items-center gap-1.5 border border-accent/50 px-3 py-1.5 text-[11px] font-bold text-accent transition-colors hover:bg-accent/10">
              <Plus className="h-3.5 w-3.5" />
              原価を追加
            </button>
          </div>
          <div className="space-y-1.5">
            {costs.map((c) => (
              <div key={c.id} data-testid={`cost-row-${c.id}`} className="flex items-center gap-3 border border-line bg-ink px-3 py-2">
                <span className="font-mono text-[11px] text-gray-500">{c.date}</span>
                <span className="border border-line px-1.5 py-0.5 text-[9px] text-gray-400">{c.category}</span>
                <span className="flex-1 truncate text-xs text-gray-300">{c.description}</span>
                <span className="font-mono text-xs font-bold text-white">{yen(c.amount)}</span>
                <button data-testid={`cost-edit-${c.id}`} onClick={() => { setCostForm({ ...EMPTY_COST, ...c }); setCostEditing(c); }} className="p-1 text-gray-500 transition-colors hover:text-accent"><Pencil className="h-3.5 w-3.5" /></button>
                <button data-testid={`cost-delete-${c.id}`} onClick={() => removeCost(c)} className="p-1 text-gray-500 transition-colors hover:text-red-400"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            ))}
            {costs.length === 0 && <p className="text-xs text-gray-600">原価が登録されていません。「原価を追加」から記録してください。</p>}
          </div>
        </Section>
      </div>

      <div className="mt-6">
        <Section title="日報（スタッフからの報告）" testid="project-detail-reports" count={daily_reports.length}>
          <div className="space-y-1.5">
            {daily_reports.map((r) => (
              <div key={r.id} data-testid={`report-row-${r.id}`} className="border border-line bg-ink px-3 py-2.5">
                <div className="flex items-center gap-3">
                  <span className="font-mono text-[11px] text-gray-500">{r.date}</span>
                  <span className="text-xs font-bold text-white">{r.staff_name}</span>
                  {r.hours > 0 && <span className="text-[10px] text-gray-500">実働 {r.hours}h</span>}
                </div>
                <p className="mt-1 whitespace-pre-wrap text-xs text-gray-400">{r.content}</p>
                {r.notes && <p className="mt-1 text-[10px] text-gray-500">備考: {r.notes}</p>}
              </div>
            ))}
            {daily_reports.length === 0 && <p className="text-xs text-gray-600">日報はまだありません。スタッフ専用画面から投稿されます。</p>}
          </div>
        </Section>
      </div>

      <div className="mt-6">
        <Section title="現場写真" testid="project-detail-photos" count={photos.length}>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-6">
            {photos.map((p) => (
              <div key={p.id} data-testid={`project-photo-${p.id}`} className="border border-line">
                <img src={`${api.defaults.baseURL}/site-photos/${p.id}/image`} alt={p.title} className="aspect-square w-full object-cover" />
                <div className="p-1.5">
                  <p className="truncate text-[10px] text-white">{p.title || p.category}</p>
                  <p className={`text-[9px] font-bold ${STATUS_COLORS[p.status]}`}>{p.status}</p>
                </div>
              </div>
            ))}
          </div>
          {photos.length === 0 && <p className="text-xs text-gray-600">写真が登録されていません。</p>}
        </Section>
      </div>

      {phaseEditing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/80 p-4 backdrop-blur-sm">
          <form data-testid="phase-form" onSubmit={savePhase} className="w-full max-w-md space-y-4 border border-line bg-coal p-6">
            <div className="flex items-center justify-between">
              <h2 className="font-mincho text-lg font-bold text-white">{phaseEditing.id ? "工程を編集" : "工程を追加"}</h2>
              <button type="button" data-testid="phase-form-close" onClick={() => setPhaseEditing(null)} className="text-gray-500 hover:text-white"><X className="h-5 w-5" /></button>
            </div>
            <label className="block">
              <span className="font-mono text-[10px] tracking-[0.3em] text-gray-500">工程名 *</span>
              <input data-testid="phase-form-name" required value={phaseForm.name} onChange={(e) => setPhaseForm((f) => ({ ...f, name: e.target.value }))} className={`mt-1.5 ${inputCls}`} placeholder="例：基礎工事" />
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="font-mono text-[10px] tracking-[0.3em] text-gray-500">開始予定</span>
                <input data-testid="phase-form-start" type="date" value={phaseForm.start} onChange={(e) => setPhaseForm((f) => ({ ...f, start: e.target.value }))} className={`mt-1.5 ${inputCls}`} />
              </label>
              <label className="block">
                <span className="font-mono text-[10px] tracking-[0.3em] text-gray-500">終了予定</span>
                <input data-testid="phase-form-end" type="date" value={phaseForm.end} onChange={(e) => setPhaseForm((f) => ({ ...f, end: e.target.value }))} className={`mt-1.5 ${inputCls}`} />
              </label>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="font-mono text-[10px] tracking-[0.3em] text-gray-500">状態</span>
                <select data-testid="phase-form-status" value={phaseForm.status} onChange={(e) => setPhaseForm((f) => ({ ...f, status: e.target.value }))} className={`mt-1.5 ${inputCls}`}>
                  <option>未着手</option><option>進行中</option><option>完了</option>
                </select>
              </label>
              <label className="block">
                <span className="font-mono text-[10px] tracking-[0.3em] text-gray-500">表示順</span>
                <input data-testid="phase-form-order" type="number" value={phaseForm.order} onChange={(e) => setPhaseForm((f) => ({ ...f, order: e.target.value }))} className={`mt-1.5 ${inputCls}`} />
              </label>
            </div>
            <label className="block">
              <span className="font-mono text-[10px] tracking-[0.3em] text-gray-500">メモ</span>
              <textarea data-testid="phase-form-notes" rows={2} value={phaseForm.notes} onChange={(e) => setPhaseForm((f) => ({ ...f, notes: e.target.value }))} className={`mt-1.5 resize-y ${inputCls}`} />
            </label>
            <button data-testid="phase-form-submit" type="submit" disabled={saving} className="w-full bg-accent py-3 font-display text-sm font-bold tracking-[0.2em] text-ink transition-colors hover:bg-accent-hover disabled:opacity-50">
              {saving ? "保存中..." : "保存する"}
            </button>
          </form>
        </div>
      )}

      {costEditing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/80 p-4 backdrop-blur-sm">
          <form data-testid="cost-form" onSubmit={saveCost} className="w-full max-w-md space-y-4 border border-line bg-coal p-6">
            <div className="flex items-center justify-between">
              <h2 className="font-mincho text-lg font-bold text-white">{costEditing.id ? "原価を編集" : "原価を追加"}</h2>
              <button type="button" data-testid="cost-form-close" onClick={() => setCostEditing(null)} className="text-gray-500 hover:text-white"><X className="h-5 w-5" /></button>
            </div>
            <label className="block">
              <span className="font-mono text-[10px] tracking-[0.3em] text-gray-500">内容 *</span>
              <input data-testid="cost-form-description" required value={costForm.description} onChange={(e) => setCostForm((f) => ({ ...f, description: e.target.value }))} className={`mt-1.5 ${inputCls}`} placeholder="例：砕石 10m3" />
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="font-mono text-[10px] tracking-[0.3em] text-gray-500">費目</span>
                <select data-testid="cost-form-category" value={costForm.category} onChange={(e) => setCostForm((f) => ({ ...f, category: e.target.value }))} className={`mt-1.5 ${inputCls}`}>
                  {COST_CATEGORIES.map((c) => (<option key={c}>{c}</option>))}
                </select>
              </label>
              <label className="block">
                <span className="font-mono text-[10px] tracking-[0.3em] text-gray-500">金額（円）*</span>
                <input data-testid="cost-form-amount" type="number" min="0" required value={costForm.amount} onChange={(e) => setCostForm((f) => ({ ...f, amount: e.target.value }))} className={`mt-1.5 ${inputCls}`} />
              </label>
            </div>
            <label className="block">
              <span className="font-mono text-[10px] tracking-[0.3em] text-gray-500">発生日</span>
              <input data-testid="cost-form-date" type="date" value={costForm.date} onChange={(e) => setCostForm((f) => ({ ...f, date: e.target.value }))} className={`mt-1.5 ${inputCls}`} />
            </label>
            <label className="block">
              <span className="font-mono text-[10px] tracking-[0.3em] text-gray-500">メモ</span>
              <textarea data-testid="cost-form-note" rows={2} value={costForm.note} onChange={(e) => setCostForm((f) => ({ ...f, note: e.target.value }))} className={`mt-1.5 resize-y ${inputCls}`} />
            </label>
            <button data-testid="cost-form-submit" type="submit" disabled={saving} className="w-full bg-accent py-3 font-display text-sm font-bold tracking-[0.2em] text-ink transition-colors hover:bg-accent-hover disabled:opacity-50">
              {saving ? "保存中..." : "保存する"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
